import type { ReactNode } from 'react'
import { chipWord, type StatusDomain, type StatusIcon, type StatusWord } from '../lib/status'

// The status chip: icon, word and colour, read from lib/status.ts.
//
// # WHY THE ICON
//
// The kit's status label carried a coloured dot and the word. A dot is the same
// shape for every state, so a reader who cannot tell amber from red read
// "Overdue" and "Rejected" as the same signal until they read the word. The
// icon is the second signal and colour the third (WCAG 1.4.1). It is drawn
// here, inline, because the console ships with no icon font and an on-premises
// install loads nothing from outside.

const PATHS: Record<StatusIcon, ReactNode> = {
  dot: <circle cx="8" cy="8" r="3" fill="currentColor" stroke="none" />,
  check: <path d="m4 8.5 2.5 2.5L12 5.5" />,
  clock: (
    <>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 5v3.2l2 1.3" />
    </>
  ),
  alert: (
    <>
      <path d="M8 2.5 14 13H2z" />
      <path d="M8 6.5v3M8 11.3v.2" />
    </>
  ),
  pause: <path d="M6 4.5v7M10 4.5v7" />,
  cross: <path d="m5 5 6 6M11 5l-6 6" />,
  send: <path d="M2.5 8 13.5 3l-3.5 10-2.2-4.3z" />,
  pen: <path d="M3 13l1-3.5L10.5 3 13 5.5 6.5 12zM9.5 4l2.5 2.5" />,
  inbox: (
    <>
      <path d="M2.5 9h3l1 2h3l1-2h3" />
      <path d="M4 3.5h8l1.5 5.5V12.5h-11V9z" />
    </>
  ),
  hourglass: <path d="M4.5 2.5h7M4.5 13.5h7M5 2.5c0 3 6 3 6 5.5s-6 2.5-6 5.5M11 2.5c0 3-6 3-6 5.5s6 2.5 6 5.5" />,
  flag: <path d="M4 14V2.5M4 3h7l-1.5 2.5L11 8H4" />,
  exit: <path d="M9.5 3H3.5v10h6M7 8h6.5M11 5.5 13.5 8 11 10.5" />,
}

export function StatusGlyph({ icon }: { icon: StatusIcon }) {
  return (
    <svg
      className="st-icon"
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[icon] ?? PATHS.dot}
    </svg>
  )
}

// A chip for a word already chosen.
export function Chip({ s, title, children }: { s: StatusWord; title?: string; children?: ReactNode }) {
  return (
    <span className={'kit-status st-chip kit-' + s.tone} title={title} data-status-word={s.word}>
      <StatusGlyph icon={s.icon} />
      {children ?? s.word}
    </span>
  )
}

// A chip for a stored code. late lays Overdue over a questionnaire still with
// the vendor or a finding still owed, the one overlay the vocabulary has.
export function StatusChip({
  domain,
  code,
  late,
  title,
}: {
  domain: StatusDomain | string
  code: string | null | undefined
  late?: boolean
  title?: string
}) {
  return <Chip s={chipWord(domain, code, late)} title={title} />
}
